import { Mail } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading.jsx";
import CTABanner from "../components/ui/CTABanner.jsx";
import EnquiryForm from "../components/EnquiryForm.jsx";
import LocationCard from "../components/cards/LocationCard.jsx";
import { Reveal } from "../components/motion/Reveal.jsx";
import useSeo from "../hooks/useSeo.js";
import { CONTACT, CLUBS } from "../content.js";

export default function Contact() {
  useSeo({
    title: "Contact | Oneiros Sports Club Surat — Adajan, Katargam & Varachha",
    description:
      "Have a question about memberships, training programs or facilities at Oneiros Sports Club? Share your details and our team will get in touch. Clubs in Adajan, Katargam & Varachha, Surat.",
    image: "/oneiros/adajan-hero.jpg",
  });
  return (
    <>
      <section className="mx-auto max-w-[1440px] px-6 pb-16 pt-36 lg:px-12 lg:pt-48">
        <SectionHeading
          eyebrow="Contact"
          lines={["We Are Here", "to Help You."]}
          copy="Have a question about memberships, training programs or facilities at Oneiros Sports Club? Share your details and our team will get in touch."
        />
      </section>

      <section className="mx-auto grid max-w-[1440px] gap-16 px-6 pb-24 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="mb-8 font-display text-3xl text-navy">Send a Message</h2>
          </Reveal>
          <EnquiryForm testId="contact-form" />
          <Reveal delay={0.15}>
            <p className="mt-6 flex items-center gap-2 text-xs text-faint">
              <Mail className="h-3.5 w-3.5 text-brand" />
              Enquiries are also emailed directly to our team at {CONTACT.email}
            </p>
          </Reveal>
        </div>

        <div className="flex flex-col gap-10 lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="grain rounded-xl bg-navy p-8 text-white md:p-10" data-testid="contact-talk-to-team">
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-[2px] w-8 bg-brand" /> Talk to Our Team
              </p>
              <ul className="mt-6 flex flex-col gap-4">
                {CONTACT.clubs
                  .filter((c) => c.phone)
                  .map((c) => (
                    <li key={c.id} className="flex items-center justify-between border-b border-white/10 pb-4 last:border-0">
                      <span className="text-sm uppercase tracking-[0.15em] text-white/60">{c.name}</span>
                      <a data-testid={`contact-phone-${c.id}`} href={c.phoneHref} className="font-display text-xl transition-colors hover:text-brand">
                        {c.phone}
                      </a>
                    </li>
                  ))}
              </ul>
              <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="mt-6 inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-brand">
                <Mail className="h-4 w-4 text-brand" /> {CONTACT.email}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="border border-hairline p-8" data-testid="contact-timings">
              <p className="eyebrow mb-5">Club Hours</p>
              <ul className="flex flex-col gap-3 text-sm text-body">
                <li className="flex justify-between border-b border-hairline pb-3">
                  <span>Adajan</span> <span className="text-mute">Morning 6 AM – 11 PM</span>
                </li>
                <li className="flex justify-between border-b border-hairline pb-3">
                  <span>Katargam</span> <span className="text-mute">Morning 6 AM – 10 PM</span>
                </li>
                <li className="flex justify-between">
                  <span>Varachha</span> <span className="text-mute">9:00 AM to 7:30 PM</span>
                </li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-hairline bg-mist py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <SectionHeading eyebrow="Visit Oneiros" lines={["Visit Oneiros."]} copy="Three destinations across Surat. Find the club closest to you." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {CLUBS.map((c, i) => (
              <LocationCard
                key={c.id}
                club={{ ...c, index: String(i + 1).padStart(2, "0") }}
                testId={`contact-location-${c.id}`}
              />
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        eyebrow="Contact"
        lines={["Let's Build a", "Stronger Tomorrow."]}
        primary={{ label: "Enquire Now", to: "/contact" }}
        secondary={{ label: "Explore Blue Membership", to: "/blue-membership" }}
        testId="contact-final-cta"
      />
    </>
  );
}
